// The components this renderer can draw and play (contracts/README.md,
// "Components and capabilities"). Web clients send this list as
// `X-Client-Capabilities`; the server only serves specs whose `requires` is a
// subset of it. Add a component here only once it renders AND plays.

import type { PuzzleSpec } from "./types";

export const CAPABILITIES = [
  "board.none@1",
  "board.single@1",
  "board.sequence@1",
  "board.matrix@1",
  "board.analogy@1",
  "board.grid@1",
  "board.pair@1",
  "board.scatter@1",
  "board.cards@1",
  "board.graph@1",
  // Launch types (contracts/launch-types.md §1). board.grid@2 is board.grid@1 plus `regions`.
  "board.grid@2",
  "board.scene@1",
  "board.hive@1",
  "board.wheel@1",
  "glyph.text@1",
  "glyph.shape@1",
  "glyph.dots@1",
  "glyph.lines@1",
  "glyph.cells@1",
  "glyph.stack@1",
  "glyph.iso@1",
  "glyph.swatch@1",
  "glyph.jug@1",
  "glyph.slot@1",
  "glyph.empty@1",
  "glyph.clue@1",
  "glyph.cube@1",
  "answer.choice@1",
  "answer.cellSet@1",
  "answer.number@1",
  "answer.digits@1",
  "answer.fill@1",
  "answer.sequence@1",
  "answer.rows@1",
  "phase.watch@1",
  "phase.recall@1",
] as const;

export type Capability = (typeof CAPABILITIES)[number];

/** Value for the `X-Client-Capabilities` request header. */
export const CAPABILITIES_HEADER = CAPABILITIES.join(",");

/**
 * The colour-blind preference token (contracts/launch-types.md §1). Not a component: a client sends it
 * next to its capabilities only while the player's "Colour-blind friendly" setting is on, and the server
 * then never serves colour-only types (`accessibility.colorOnly`: Stroop, Color Match).
 */
export const PREF_COLORBLIND = "pref.colorblind@1";

/** `X-Client-Capabilities` with the player's preferences: `pref.colorblind@1` appended when `colorblind`. */
export function capabilitiesHeader(prefs: { colorblind?: boolean } = {}): string {
  return prefs.colorblind ? `${CAPABILITIES_HEADER},${PREF_COLORBLIND}` : CAPABILITIES_HEADER;
}

/** Whether an `X-Client-Capabilities` value (from a browser request) carries `pref.colorblind@1`. */
export const wantsColorblind = (header: string | null | undefined) =>
  typeof header === "string" && header.split(",").some((t) => t.trim() === PREF_COLORBLIND);

const SUPPORTED = new Set<string>(CAPABILITIES);

export const isSupported = (component: string) => SUPPORTED.has(component);

/** Components a spec requires that this renderer lacks (empty = fully supported). */
export function missingCapabilities(spec: Pick<PuzzleSpec, "requires"> | null | undefined): string[] {
  const requires = Array.isArray(spec?.requires) ? spec.requires : [];
  return requires.filter((r) => typeof r !== "string" || !SUPPORTED.has(r)).map(String);
}
